import React, { useState, useRef, useEffect } from 'react';
import {
    Modal,
    View,
    Text,
    TouchableOpacity,
    FlatList,
    StyleSheet,
    Animated,
    Dimensions,
    Easing,
} from 'react-native';
import FontAwesome from '@expo/vector-icons/FontAwesome';
import { Colors } from '@/constants/theme';
import type { AvailablePhone } from '@/services/calls/types';

const SCREEN_HEIGHT = Dimensions.get('window').height;

interface PhonePickerModalProps {
    visible: boolean;
    /**
     * Numbers to choose from (from `fetchAvailablePhones`). The caller fetches
     * them *before* opening this modal so a single-number client is called
     * straight through with no picker flashing by — this only renders a choice.
     */
    phones: AvailablePhone[];
    onClose: () => void;
    /** The guide confirmed a number; fires once the dialog has closed. */
    onSelect: (phone: AvailablePhone) => void;
}

/** Index of the number to preselect: the client's usual one, else the first. */
function defaultIndex(phones: AvailablePhone[]): number | null {
    if (phones.length === 0) return null;
    const idx = phones.findIndex(p => p.is_default);
    return idx >= 0 ? idx : 0;
}

/**
 * Centered-dialog picker for the PSTN number to dial (web / desktop). Mirrors
 * [TransferChatModal.web] (same card, backdrop, radio rows and footer button).
 */
export function PhonePickerModal({ visible, phones, onClose, onSelect }: PhonePickerModalProps) {
    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

    const slideAnim = useRef(new Animated.Value(SCREEN_HEIGHT)).current;
    const fadeAnim  = useRef(new Animated.Value(0)).current;

    useEffect(() => {
        if (visible) {
            setSelectedIndex(defaultIndex(phones));
            Animated.sequence([
                Animated.timing(fadeAnim, { toValue: 1, duration: 250, useNativeDriver: true }),
                Animated.timing(slideAnim, { toValue: 0, duration: 350, easing: Easing.out(Easing.cubic), useNativeDriver: true }),
            ]).start();
        }
    }, [visible, phones, fadeAnim, slideAnim]);

    const handleClose = (after?: () => void) => {
        Animated.sequence([
            Animated.timing(slideAnim, { toValue: SCREEN_HEIGHT, duration: 300, easing: Easing.in(Easing.cubic), useNativeDriver: true }),
            Animated.timing(fadeAnim, { toValue: 0, duration: 200, useNativeDriver: true }),
        ]).start(() => {
            setSelectedIndex(null);
            onClose();
            after?.();
        });
    };

    const selected = selectedIndex != null ? phones[selectedIndex] : undefined;

    const handleCall = () => {
        if (!selected) return;
        // Close first, then hand off — the desktop shell opens a separate call
        // window and the dialog shouldn't linger behind it.
        handleClose(() => onSelect(selected));
    };

    const renderPhone = ({ item, index }: { item: AvailablePhone; index: number }) => {
        const isSelected = selectedIndex === index;
        return (
            <TouchableOpacity style={styles.row} activeOpacity={0.7} onPress={() => setSelectedIndex(index)}>
                <View style={styles.phoneIcon}>
                    <FontAwesome name="phone" size={18} color={Colors.brand} />
                </View>
                <View style={styles.phoneInfo}>
                    <Text style={styles.phoneNumber}>{item.phone}</Text>
                    {item.is_default && (
                        <Text style={styles.phoneCaption}>Default number</Text>
                    )}
                </View>
                {item.is_default && (
                    <View style={styles.defaultBadge}>
                        <Text style={styles.defaultBadgeText}>Default</Text>
                    </View>
                )}
                <View style={[styles.radio, isSelected && styles.radioSelected]}>
                    {isSelected && <View style={styles.radioDot} />}
                </View>
            </TouchableOpacity>
        );
    };

    return (
        <Modal visible={visible} transparent animationType="none" onRequestClose={() => handleClose()}>
            <View style={styles.container}>
                <Animated.View style={[StyleSheet.absoluteFill, styles.backdrop, { opacity: fadeAnim }]}>
                    <TouchableOpacity style={{ flex: 1 }} activeOpacity={1} onPress={() => handleClose()} />
                </Animated.View>

                <Animated.View style={[styles.card, { transform: [{ translateY: slideAnim }] }]}>
                    <View style={styles.header}>
                        <Text style={styles.title}>UniPhone Call</Text>
                        <TouchableOpacity onPress={() => handleClose()} hitSlop={12}>
                            <FontAwesome name="times" size={18} color="#333" />
                        </TouchableOpacity>
                    </View>

                    <Text style={styles.subtitle}>Choose the number to call</Text>

                    <FlatList
                        data={phones}
                        keyExtractor={(item, index) => `${item.id ?? 'lead'}-${item.phone}-${index}`}
                        style={styles.list}
                        renderItem={renderPhone}
                        ItemSeparatorComponent={() => <View style={styles.separator} />}
                        ListEmptyComponent={
                            <Text style={styles.emptyText}>No phone numbers available</Text>
                        }
                    />

                    <View style={styles.footer}>
                        <TouchableOpacity
                            style={[styles.callBtn, !selected && styles.callBtnDisabled]}
                            activeOpacity={0.8}
                            onPress={handleCall}
                            disabled={!selected}
                        >
                            <FontAwesome name="phone" size={16} color="#fff" />
                            <Text style={styles.callBtnText}>Call</Text>
                        </TouchableOpacity>
                    </View>
                </Animated.View>
            </View>
        </Modal>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    backdrop: {
        backgroundColor: 'rgba(0,0,0,0.45)',
    },
    card: {
        width: '100%',
        maxWidth: 480,
        maxHeight: '82%',
        backgroundColor: '#fff',
        borderRadius: 16,
        overflow: 'hidden',
    },
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 20,
        paddingVertical: 16,
    },
    title: {
        fontSize: 17,
        fontWeight: '700',
        color: '#1A1A1A',
    },
    subtitle: {
        fontSize: 14,
        color: '#888',
        paddingHorizontal: 20,
        marginBottom: 8,
    },
    emptyText: {
        textAlign: 'center',
        color: '#888',
        marginVertical: 32,
        fontSize: 14,
    },
    list: {
        flexGrow: 0,
    },
    row: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 20,
        paddingVertical: 12,
        gap: 12,
    },
    phoneIcon: {
        width: 44,
        height: 44,
        borderRadius: 22,
        backgroundColor: 'rgba(20, 165, 198, 0.12)',
        justifyContent: 'center',
        alignItems: 'center',
    },
    phoneInfo: {
        flex: 1,
    },
    phoneNumber: {
        fontSize: 15,
        fontWeight: '600',
        color: '#1A1A1A',
    },
    phoneCaption: {
        fontSize: 13,
        color: '#888',
        marginTop: 2,
    },
    defaultBadge: {
        backgroundColor: '#E7F8EE',
        borderRadius: 4,
        paddingHorizontal: 6,
        paddingVertical: 2,
    },
    defaultBadgeText: {
        fontSize: 11,
        fontWeight: '600',
        color: '#1FA855',
    },
    radio: {
        width: 22,
        height: 22,
        borderRadius: 11,
        borderWidth: 2,
        borderColor: '#CCC',
        justifyContent: 'center',
        alignItems: 'center',
    },
    radioSelected: {
        borderColor: Colors.brand,
    },
    radioDot: {
        width: 11,
        height: 11,
        borderRadius: 6,
        backgroundColor: Colors.brand,
    },
    separator: {
        height: StyleSheet.hairlineWidth,
        backgroundColor: '#EFEFEF',
        marginLeft: 76,
    },
    footer: {
        padding: 16,
        borderTopWidth: StyleSheet.hairlineWidth,
        borderTopColor: '#EFEFEF',
    },
    callBtn: {
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 8,
        backgroundColor: Colors.brand,
        borderRadius: 10,
        paddingVertical: 15,
    },
    callBtnDisabled: {
        opacity: 0.45,
    },
    callBtnText: {
        color: '#fff',
        fontSize: 16,
        fontWeight: '600',
    },
});
